import { useMutation, useQueryClient } from "@tanstack/react-query";
import { XCircle } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { toast } from "@/components/ui/Toast";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { formatDateTime } from "@/lib/format";
import type { Share } from "@/types";

// ─────────────────────────────────────────────
// ActiveSharesTable
//
// Presentational table of share links for an assessment. Data is fetched by
// the parent (host tab) and passed in as `shares`.
// Columns: recipient email, allowed domains, expiry, status, access count,
// last accessed, revoke button.
// Design: §8.5 Sharing UI
// ─────────────────────────────────────────────

interface ActiveSharesTableProps {
  assessmentId: string;
  shares: Share[];
  isLoading?: boolean;
}

export function ActiveSharesTable({ assessmentId, shares, isLoading }: ActiveSharesTableProps) {
  const queryClient = useQueryClient();
  const { t, language } = useTranslation();
  const [revokeId, setRevokeId] = useState<string | null>(null);

  const revokeMutation = useMutation({
    mutationFn: (shareId: string) => api.delete(`/shares/${shareId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment-shares", assessmentId] });
      toast.success(t("sharing.shareRevoked"));
      setRevokeId(null);
    },
    onError: (err: Error) => toast.error(t("sharing.revokeFailed"), err.message),
  });

  if (isLoading) {
    return (
      <div className="py-6 text-center text-body text-text-muted">{t("sharing.loadingShares")}</div>
    );
  }

  if (shares.length === 0) {
    return (
      <div className="rounded-card border border-border-default bg-card px-5 py-8 text-center">
        <p className="text-body text-text-muted">{t("sharing.noActiveShares")}</p>
      </div>
    );
  }

  const now = Date.now();

  return (
    <>
      <div className="overflow-hidden rounded-card border border-border-default bg-card">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-body">
            <thead>
              <tr className="border-b border-border-default">
                <th className="px-5 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("sharing.recipient")}
                </th>
                <th className="px-5 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("sharing.domains")}
                </th>
                <th className="px-5 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("sharing.expires")}
                </th>
                <th className="px-5 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("sharing.status")}
                </th>
                <th className="px-5 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("sharing.accesses")}
                </th>
                <th className="px-5 py-3 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">
                  {t("sharing.lastAccessed")}
                </th>
                <th className="w-28 px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {shares.map((share) => {
                const isRevoked = share.status === "REVOKED";
                const isExpired = new Date(share.expiresAt).getTime() < now;
                const dimmed = isRevoked || isExpired;
                return (
                  <tr
                    key={share.id}
                    className={cn(
                      "border-b border-border-subtle last:border-0",
                      dimmed && "opacity-60"
                    )}
                  >
                    <td className="px-5 py-3.5 text-text-primary">{share.externalEmail}</td>
                    <td className="px-5 py-3.5">
                      {share.allowedDomains.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {share.allowedDomains.map((d) => (
                            <span
                              key={d}
                              className="rounded-badge bg-hover px-2 py-0.5 font-mono text-[11px] text-text-secondary"
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-text-muted">{t("sharing.allDomains")}</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-text-secondary">
                      <div className="flex flex-col">
                        <span>{formatDateTime(share.expiresAt, language)}</span>
                        {isExpired && !isRevoked && (
                          <span className="text-[11px] font-medium" style={{ color: "var(--warning)" }}>
                            {t("sharing.expired")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusBadge type="share" status={share.status} />
                    </td>
                    <td className="px-5 py-3.5 text-text-secondary">{share.accessCount}</td>
                    <td className="px-5 py-3.5 text-text-secondary">
                      {share.lastAccessedAt ? (
                        formatDateTime(share.lastAccessedAt, language)
                      ) : (
                        <span className="text-text-muted">{t("common.never")}</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevokeId(share.id)}
                        disabled={isRevoked}
                        className={cn(
                          "text-danger hover:bg-danger-bg hover:text-danger",
                          isRevoked && "opacity-40"
                        )}
                      >
                        <XCircle className="mr-1.5 h-3.5 w-3.5" />
                        {t("sharing.revoke")}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(revokeId)}
        onOpenChange={(v) => !v && setRevokeId(null)}
        title={t("sharing.revokeShareTitle")}
        description={t("sharing.revokeShareDesc")}
        confirmLabel={t("sharing.revokeShareTitle")}
        isDestructive
        onConfirm={() => revokeId && revokeMutation.mutate(revokeId)}
        isLoading={revokeMutation.isPending}
      />
    </>
  );
}
